import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { textColorThemes } from '../data/colorThemes';
import { decorationThemes } from '../utils/decorationUtils';

const { 
  FiArrowLeft, FiType, FiSmartphone, FiMonitor, FiDroplet, FiLayout, 
  FiPlus, FiSmile, FiImage, FiSquare, FiCheck, FiGrid, FiRefreshCw, 
  FiZap, FiEdit3, FiUser 
} = FiIcons;

const fonts = [
  { name: 'Inter', value: 'Inter, sans-serif' },
  { name: 'Playfair Display', value: 'Playfair Display, serif' },
  { name: 'Montserrat', value: 'Montserrat, sans-serif' },
  { name: 'Lora', value: 'Lora, serif' },
  { name: 'Poppins', value: 'Poppins, sans-serif' },
  { name: 'Crimson Text', value: 'Crimson Text, serif' },
  { name: 'Permanent Marker', value: 'Permanent Marker, cursive' },
  { name: 'Anton', value: 'Anton, sans-serif' },
  { name: 'Bebas Neue', value: 'Bebas Neue, sans-serif' },
  { name: 'Righteous', value: 'Righteous, cursive' },
];

const defaultBackgrounds = [
  { name: 'Ocean Blue', type: 'gradient', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { name: 'Sunset', type: 'gradient', value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { name: 'Vibrant Orange', type: 'color', value: '#f97316' },
  { name: 'Abstract Fluid', type: 'image', value: 'https://images.unsplash.com/photo-1541701494587-cb58502866ab?w=800' },
  { name: 'Neon Lights', type: 'image', value: 'https://images.unsplash.com/photo-1550684848-fac1c5b4e853?w=800' },
  { name: 'Galaxy', type: 'image', value: 'https://images.unsplash.com/photo-1464802686167-b939a6910659?w=800' },
  { name: 'Paper Texture', type: 'image', value: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800' },
  { name: 'Pure White', type: 'color', value: '#ffffff' },
  { name: 'Deep Black', type: 'color', value: '#000000' },
  { name: 'Dark Slate', type: 'color', value: '#2d2d2d' }
];

const tabs = [
  { id: 'content', label: 'Quote', icon: FiEdit3 },
  { id: 'layout', label: 'Layout', icon: FiLayout },
  { id: 'text', label: 'Text', icon: FiType },
  { id: 'background', label: 'Bg', icon: FiImage },
  { id: 'colors', label: 'Colors', icon: FiDroplet },
];

function CustomizationPanel({ 
  quote, author, onQuoteChange, onAuthorChange, 
  customizations, onCustomizationChange, onBack, onPreview 
}) {
  const [activeTab, setActiveTab] = useState('content');
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) return alert("Image is too large (Max 5MB).");
      const reader = new FileReader();
      reader.onload = (event) => onCustomizationChange('background', { 
        name: 'Custom', type: 'image', value: event.target.result, isCustom: true 
      });
      reader.readAsDataURL(file);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'content':
        return (
          <div className="space-y-6 animate-fadeIn w-full">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                  <SafeIcon icon={FiEdit3} className="w-3 h-3" /> Quote Text
                </label>
                <textarea 
                  value={quote}
                  onChange={(e) => onQuoteChange(e.target.value)}
                  placeholder="Enter your quote..."
                  rows={4}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                  <SafeIcon icon={FiUser} className="w-3 h-3" /> Author
                </label>
                <input 
                  type="text"
                  value={author}
                  onChange={(e) => onAuthorChange(e.target.value)}
                  placeholder="Author name..."
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium"
                />
              </div>
            </div>
          </div>
        );

      case 'layout':
        return (
          <div className="space-y-6 animate-fadeIn w-full">
            <div className="space-y-3">
              <label className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <SafeIcon icon={FiSmartphone} className="w-4 h-4 text-indigo-500" /> Orientation
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'portrait', icon: FiSmartphone, label: 'Portrait' },
                  { value: 'square', icon: FiSquare, label: 'Square' },
                  { value: 'landscape', icon: FiMonitor, label: 'Wide' }
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => onCustomizationChange('orientation', opt.value)}
                    className={`p-2 py-3 rounded-xl border-2 transition-all flex flex-col items-center justify-center gap-1 group ${
                      customizations.orientation === opt.value 
                        ? 'border-indigo-500 bg-indigo-50/50 text-indigo-700 shadow-sm' 
                        : 'border-slate-100 text-slate-500 hover:border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <SafeIcon icon={opt.icon} className={`w-4 h-4 ${customizations.orientation === opt.value ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-600'}`} />
                    <span className="block text-[9px] font-bold uppercase tracking-tight">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <SafeIcon icon={FiGrid} className="w-4 h-4 text-indigo-500" /> Design Style
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'standard', label: 'Classic' },
                  { value: 'dynamic', label: 'Dynamic' },
                  { value: 'brutal', label: 'Neo-Brutal' },
                  { value: 'polaroid', label: 'Polaroid' },
                  { value: 'retro', label: 'Digi-Glitch' },
                  { value: 'bordered', label: 'Frame' },
                  { value: 'sticker', label: 'Sticker' },
                  { value: 'grunge', label: 'Grunge' },
                  { value: 'ribbon', label: 'Ribbon' },
                  { value: 'paint', label: 'Paint' },
                  { value: 'magazine', label: 'Editorial' },
                  { value: 'glass', label: 'Glass' }
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => onCustomizationChange('typographyType', opt.value)}
                    className={`py-2 px-2.5 rounded-xl border-2 transition-all text-xs font-bold uppercase tracking-wide text-left flex items-center justify-between group ${
                      customizations.typographyType === opt.value 
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm' 
                        : 'border-slate-100 text-slate-500 hover:border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center min-w-0 flex-1">
                      <span className="truncate mr-1">{opt.label}</span>
                      {opt.value !== 'standard' && customizations.typographyType === opt.value && (
                        <motion.div
                          whileHover={{ rotate: 180 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onCustomizationChange('dynamicSeed', Math.random());
                          }}
                          className="p-1 bg-indigo-100 text-indigo-600 rounded-lg hover:bg-indigo-200 transition-colors ml-1"
                          title="Re-apply Style Variant"
                        >
                          <SafeIcon icon={FiRefreshCw} className="w-3 h-3" />
                        </motion.div>
                      )}
                    </div>
                    {customizations.typographyType === opt.value && <SafeIcon icon={FiCheck} className="w-3 h-3 text-indigo-600" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'text':
        return (
          <div className="space-y-6 animate-fadeIn w-full">
            <div className="space-y-3">
              <label className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <SafeIcon icon={FiType} className="w-4 h-4 text-indigo-500" /> Typography
              </label>
              <div className="grid grid-cols-1 gap-2 bg-slate-50 p-2 rounded-xl border border-slate-100 max-h-[200px] overflow-y-auto custom-scrollbar">
                {fonts.map((font) => (
                  <button
                    key={font.value}
                    onClick={() => onCustomizationChange('font', font.value)}
                    className={`p-2.5 rounded-lg transition-all text-left flex items-center justify-between ${
                      customizations.font === font.value 
                        ? 'bg-white shadow-sm text-indigo-700 ring-1 ring-indigo-100' 
                        : 'hover:bg-white/50 text-slate-600'
                    }`}
                  >
                    <span className="text-sm truncate" style={{ fontFamily: font.value }}>{font.name}</span>
                    {customizations.font === font.value && <div className="w-2 h-2 rounded-full bg-indigo-500"></div>}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-3 space-y-4 border border-slate-100">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Size</label>
                  <span className="text-xs font-bold bg-white text-indigo-600 px-2 py-1 rounded border border-indigo-100">{customizations.fontSize}px</span>
                </div>
                <input 
                  type="range" 
                  min="16" 
                  max="140" 
                  value={customizations.fontSize} 
                  onChange={(e) => onCustomizationChange('fontSize', parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block">Casing</label>
                <div className="grid grid-cols-2 gap-2">
                  {[{value: 'layout', label: 'Default'}, {value: 'uppercase', label: 'AA'}, {value: 'lowercase', label: 'aa'}, {value: 'camelcase', label: 'Aa'}].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => onCustomizationChange('textTransform', opt.value)}
                      className={`py-2 px-2 rounded-lg border transition-all text-xs font-bold ${
                        customizations.textTransform === opt.value 
                          ? 'border-indigo-500 bg-white text-indigo-700 shadow-sm' 
                          : 'border-slate-200 bg-slate-100 text-slate-500 hover:border-slate-300'
                      }`}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'background':
        return (
          <div className="space-y-6 animate-fadeIn w-full">
            <div className="grid grid-cols-2 gap-2">
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center justify-center p-3 rounded-xl border-2 border-dashed border-indigo-300 bg-indigo-50 hover:bg-indigo-100 transition-all group"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4 text-indigo-500 mb-1 group-hover:scale-110 transition-transform" />
                <span className="text-xs font-bold text-indigo-700 uppercase tracking-tight">Upload Img</span>
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" className="hidden" />
              </button>
              <button 
                onClick={() => onCustomizationChange('showDecorations', !customizations.showDecorations)}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all ${
                  customizations.showDecorations ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <SafeIcon icon={FiSmile} className={`w-4 h-4 mb-1 ${customizations.showDecorations ? 'text-indigo-600' : 'text-slate-400'}`} />
                <span className={`text-xs font-bold uppercase tracking-tight ${customizations.showDecorations ? 'text-indigo-700' : 'text-slate-500'}`}>
                  {customizations.showDecorations ? 'Deco On' : 'Deco Off'}
                </span>
              </button>
            </div>

            {customizations.showDecorations && (
              <div className="space-y-3 animate-slideDown">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                  <SafeIcon icon={FiZap} className="w-3 h-3" /> Decoration Theme
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {decorationThemes.map((theme) => (
                    <button
                      key={theme.id}
                      onClick={() => onCustomizationChange('decorationStyle', theme.id)}
                      className={`p-2 rounded-xl border transition-all flex flex-col items-center justify-center gap-1 ${
                        customizations.decorationStyle === theme.id 
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-md scale-105' 
                          : 'bg-slate-50 text-slate-500 border-slate-100 hover:bg-white hover:border-slate-200'
                      }`}
                    >
                      <SafeIcon icon={FiIcons[theme.icon]} className="w-3.5 h-3.5" />
                      <span className="text-[8px] font-bold uppercase tracking-tighter truncate w-full text-center">{theme.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <label className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <SafeIcon icon={FiImage} className="w-4 h-4 text-indigo-500" /> Background Presets
              </label>
              <div className="grid grid-cols-2 gap-2">
                {defaultBackgrounds.map((bg) => (
                  <button
                    key={bg.name}
                    onClick={() => onCustomizationChange('background', bg)}
                    className={`relative aspect-square rounded-xl overflow-hidden shadow-sm transition-all group ${
                      customizations.background.name === bg.name ? 'ring-4 ring-indigo-500 ring-offset-2 scale-95' : 'hover:scale-105'
                    }`}
                  >
                    <div className="w-full h-full" style={{ background: bg.type === 'image' ? `url(${bg.value}) center/cover` : bg.value }} />
                    {customizations.background.name === bg.name && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <SafeIcon icon={FiCheck} className="w-6 h-6 text-white drop-shadow-md" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'colors':
        return (
          <div className="space-y-6 animate-fadeIn w-full">
            <label className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <SafeIcon icon={FiDroplet} className="w-4 h-4 text-indigo-500" /> Theme Palette
            </label>
            <div className="grid grid-cols-1 gap-2">
              {textColorThemes.map((theme) => (
                <button
                  key={theme.id}
                  onClick={() => {
                    onCustomizationChange('textColor', theme.main);
                    onCustomizationChange('accentColor', theme.accent);
                  }}
                  className={`p-3 rounded-xl border-2 transition-all text-left flex items-center gap-3 group ${
                    customizations.textColor === theme.main && customizations.accentColor === theme.accent 
                      ? 'border-indigo-500 bg-indigo-50' 
                      : 'border-slate-100 hover:border-slate-200 bg-white'
                  }`}
                >
                  <div className="flex -space-x-3 shrink-0">
                    <div className="w-8 h-8 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: theme.main }} />
                    <div className="w-8 h-8 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: theme.accent }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className={`block text-xs font-bold uppercase tracking-tight truncate ${customizations.textColor === theme.main ? 'text-indigo-900' : 'text-slate-700'}`}>{theme.name}</span>
                    <span className="text-[10px] text-slate-400 group-hover:text-slate-500 transition-colors truncate block">{theme.description}</span>
                  </div>
                  {customizations.textColor === theme.main && customizations.accentColor === theme.accent && (
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-indigo-600 ml-auto shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-3xl shadow-xl overflow-hidden flex flex-row h-[600px] md:h-[650px] max-h-[85vh] border border-slate-100 w-full">
      <div className="w-[60px] md:w-20 bg-slate-50 border-r border-slate-200 flex flex-col shrink-0 z-10">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`h-16 md:h-20 w-full flex flex-col items-center justify-center gap-1 transition-all relative outline-none focus:bg-slate-100 ${
              activeTab === tab.id 
                ? 'bg-white text-indigo-600 border-l-4 border-indigo-600 shadow-sm' 
                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
            }`}
          >
            <SafeIcon icon={tab.icon} className={`w-5 h-5 md:w-6 md:h-6 ${activeTab === tab.id ? 'scale-110' : ''} transition-transform`} />
            <span className="text-[8px] md:text-[10px] font-bold uppercase tracking-wider">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="flex-1 flex flex-col min-w-0 bg-white relative">
        <div className="flex-1 overflow-y-auto p-3 md:p-6 custom-scrollbar scroll-smooth">
          <div className="max-w-md mx-auto w-full">{renderContent()}</div>
        </div>

        <div className="p-3 border-t border-slate-100 bg-white/80 backdrop-blur-sm sticky bottom-0 z-10 flex space-x-2">
          <button
            onClick={onBack}
            className="flex-1 px-3 py-3 bg-slate-50 border border-slate-200 text-slate-600 rounded-xl font-bold text-xs uppercase flex items-center justify-center space-x-2 hover:bg-slate-100 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </button>
          <button
            onClick={onPreview}
            className="flex-[2] px-4 py-3 bg-indigo-600 text-white rounded-xl font-bold text-xs uppercase shadow-lg shadow-indigo-200 hover:bg-indigo-700 hover:shadow-indigo-300 transition-all flex items-center justify-center gap-2"
          >
            <span>Done</span>
            <SafeIcon icon={FiCheck} className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default CustomizationPanel;